import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Inbox, ShoppingBag, MapPin, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (c) => `$${(c / 100).toFixed(2)}`;
const STATUS_LABEL = {
  reviewing: "Reviewing", enroute: "En route", on_site: "On site", quoted: "Quoted", sold: "Sold",
  received: "Received", pending_verification: "Pending verification", paid: "Paid",
  shipped: "Shipped", fulfilled: "Fulfilled", cancelled: "Cancelled",
};

export default function MyAccount() {
  const { user, loading } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (loading) return <div className="grid h-[60vh] place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 md:px-8 md:py-16">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">My account</span>
      <h1 className="mt-2 font-display text-4xl font-bold text-slate-900 md:text-5xl wavy">Hi {user.name?.split(" ")[0] || ""} 👋</h1>
      <p className="mt-2 text-slate-600">Track your quote requests and orders below.</p>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold text-slate-900 inline-flex items-center gap-2"><Inbox className="h-5 w-5 text-cyan-700" /> My quote requests</h2>
        {quotes.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">
            No quote requests yet. <Link to="/quote" className="text-cyan-700 hover:underline">Start one →</Link>
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {quotes.map((q) => (
              <li key={q.id} className="rounded-2xl border border-cyan-100 bg-white p-5" data-testid={`my-quote-${q.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-bold text-slate-900">{q.project_type}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(q.created_at).toLocaleDateString()}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {q.address}</span>
                    </div>
                  </div>
                  <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-800">{STATUS_LABEL[q.status] || q.status}</span>
                </div>
                {q.description && <p className="mt-3 line-clamp-2 text-sm text-slate-600">{q.description}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-slate-900 inline-flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-cyan-700" /> My orders</h2>
        {orders.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">
            No orders yet. <Link to="/shop" className="text-cyan-700 hover:underline">Visit the shop →</Link>
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {orders.map((o) => (
              <li key={o.id} className="rounded-2xl border border-cyan-100 bg-white p-5" data-testid={`my-order-${o.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-bold text-slate-900">{o.items?.length || 0} item{(o.items?.length || 0) === 1 ? "" : "s"} · {fmt(o.total_cents)}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(o.created_at).toLocaleDateString()}</span>
                      <span className="inline-flex items-center gap-1">via <code className="rounded bg-slate-100 px-1.5 font-mono text-xs">{o.payment_provider}</code></span>
                    </div>
                  </div>
                  <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-800">{STATUS_LABEL[o.status] || o.status}</span>
                </div>
                <ul className="mt-3 text-sm text-slate-600">
                  {(o.items || []).slice(0, 3).map((i) => (
                    <li key={i.product_id}>{i.quantity}× {i.name}</li>
                  ))}
                  {(o.items?.length || 0) > 3 && <li className="text-xs text-slate-400">+ {o.items.length - 3} more</li>}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
